import { useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { type ToolCatalogEntry, allToolsPath } from "~/services/agents";

/** The label pair a caller needs to render a tool: its display name and, when
 * known, the display name of the toolkit that provides it. */
export interface ResolvedTool {
  displayName: string;
  toolkitDisplayName: string | undefined;
}

/** Title-case a snake_case tool name as a last resort, e.g.
 * `some_tool_name` → "Some Tool Name". This is the ONLY client-side
 * prettification allowed to remain — the backend catalog is authoritative. */
export function titleCase(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Fetches the network-free global tool catalog once (cached) and returns a
 * resolver that maps a runtime tool name to its human-readable labels.
 *
 * The resolver never throws: when the catalog is still loading, empty, errored,
 * or simply lacks the name, it falls back to the title-cased name and an
 * undefined toolkit. This is the single source of tool-label resolution across
 * both the manifold-nextgen and assistant-v2 UIs.
 */
export function useToolCatalog(): { resolveTool: (name: string) => ResolvedTool } {
  const { data } = useFetcher<ToolCatalogEntry[]>(allToolsPath(), {
    revalidateOnFocus: false,
  });

  const catalog = useMemo(() => {
    const map = new Map<string, ToolCatalogEntry>();
    for (const entry of data ?? []) {
      map.set(entry.name, entry);
    }
    return map;
  }, [data]);

  const resolveTool = useMemo(
    () =>
      (name: string): ResolvedTool => {
        const entry = catalog.get(name);
        if (entry) {
          return {
            displayName: entry.display_name,
            toolkitDisplayName: entry.toolkit_display_name,
          };
        }
        return { displayName: titleCase(name), toolkitDisplayName: undefined };
      },
    [catalog]
  );

  return { resolveTool };
}
